import { useEffect, useState } from "react";
import axios from "axios";
import { Eye, Clock, TrendingUp, Award, RefreshCw, Trophy } from "lucide-react";
import { API } from "../../config";
import { CountryFlag } from "../shared/CountryFlag";
import { useCountryFilter } from "../../contexts/CountryFilterContext";

const fmtHours = (h) => {
  if (h === null || h === undefined) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${h.toFixed(1)} h`;
  const d = h / 24;
  return d < 7 ? `${d.toFixed(1)} g` : `${(d / 7).toFixed(1)} sett.`;
};

const Pct = ({ value, good = 50 }) => (
  <span className={`font-bold ${value >= good ? 'text-lojack-blue-400' : value >= good * 0.5 ? 'text-lojack-clause-400' : 'text-red-400'}`}>
    {value.toFixed(1)}%
  </span>
);

export const SignatureStatsWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { country, isFiltered } = useCountryFilter();

  const fetch = async () => {
    try {
      setLoading(true);
      // When the global country filter is active, scope the funnel +
      // dealer leaderboard to that country. When "Tutti i paesi" is
      // selected, omit the param so the backend short-circuits the
      // "no filter" branch.
      const params = isFiltered ? { country } : {};
      const r = await axios.get(`${API}/dashboard/signature-stats`, { params });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
    const t = setInterval(fetch, 60_000);
    return () => clearInterval(t);
    // Re-fetch whenever the global country filter changes (e.g. admin
    // picks "Italia" from the dropdown).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  if (!data) {
    return (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
        {loading ? <div className="spinner mx-auto" /> : <p className="text-gray-500 text-sm">Nessun dato</p>}
      </div>
    );
  }

  const top = data.dealer_leaderboard.filter(d => d.dealer_id).slice(0, 5);
  const totalsLine = `${data.totals.signed} firmati · ${data.totals.with_preview_opens} hanno aperto l'anteprima · ${data.totals.previewed_and_signed} hanno firmato dopo l'anteprima`;

  return (
    <div className="bg-gradient-to-br from-[#0f1f1c] via-[#0a0f0e] to-[#0f1f1c] border border-lojack-blue-500/20 rounded-2xl p-6 space-y-5" data-testid="signature-stats-widget">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-lojack-blue-500/10 flex items-center justify-center">
            <TrendingUp size={20} className="text-lojack-blue-400" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Statistiche Firma Contratti</h3>
            <p className="text-gray-500 text-xs">{totalsLine}</p>
          </div>
        </div>
        <button
          onClick={fetch}
          disabled={loading}
          className="p-2 text-gray-500 hover:text-lojack-blue-400 transition-colors"
          data-testid="signature-stats-refresh"
          title="Aggiorna"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* 3 KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="kpi-preview-rate">
          <div className="flex items-center gap-2 mb-2">
            <Eye size={16} className="text-blue-400" />
            <p className="text-gray-400 text-xs uppercase tracking-wider">Anteprima prima firma</p>
          </div>
          <p className="text-3xl font-bold text-blue-400">{data.preview_rate.toFixed(1)}%</p>
          <p className="text-gray-600 text-xs mt-1">
            {data.totals.previewed_and_signed} / {data.totals.signed} clienti firmatari
          </p>
        </div>

        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="kpi-time-to-sign">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={16} className="text-lojack-clause-400" />
            <p className="text-gray-400 text-xs uppercase tracking-wider">Tempo medio firma</p>
          </div>
          <p className="text-3xl font-bold text-lojack-clause-400">{fmtHours(data.avg_time_to_sign_hours)}</p>
          <p className="text-gray-600 text-xs mt-1">
            Mediana: {fmtHours(data.median_time_to_sign_hours)} ({data.samples_with_time} campioni)
          </p>
        </div>

        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="kpi-conversion">
          <div className="flex items-center gap-2 mb-2">
            <Award size={16} className="text-lojack-blue-400" />
            <p className="text-gray-400 text-xs uppercase tracking-wider">Conversione globale</p>
          </div>
          <p className="text-3xl font-bold text-lojack-blue-400">
            {data.totals.total_customers > 0
              ? ((data.totals.signed / data.totals.total_customers) * 100).toFixed(1)
              : '0.0'}%
          </p>
          <p className="text-gray-600 text-xs mt-1">
            {data.totals.signed} / {data.totals.total_customers} clienti totali
          </p>
        </div>
      </div>

      {/* Dealer Leaderboard */}
      {top.length > 0 && (
        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="dealer-leaderboard">
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={16} className="text-lojack-clause-400" />
            <p className="text-white font-semibold text-sm">Top Dealer per Conversione</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="text-left py-2 font-medium">#</th>
                  <th className="text-left py-2 font-medium">Dealer</th>
                  <th className="text-right py-2 font-medium">Clienti</th>
                  <th className="text-right py-2 font-medium">Firmati</th>
                  <th className="text-right py-2 font-medium">Anteprima</th>
                  <th className="text-right py-2 font-medium">Conversione</th>
                </tr>
              </thead>
              <tbody>
                {top.map((d, i) => (
                  <tr key={d.dealer_id} className="border-b border-gray-800/50" data-testid={`dealer-row-${i}`}>
                    <td className="py-2.5 text-gray-500">
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                    </td>
                    <td className="py-2.5 text-white truncate max-w-[200px]" title={d.dealer_email || d.dealer_name}>
                      <span className="inline-flex items-center gap-2">
                        <CountryFlag code={d.dealer_country} />
                        <span className="truncate">{d.dealer_name}</span>
                      </span>
                    </td>
                    <td className="py-2.5 text-right text-gray-300">{d.total_customers}</td>
                    <td className="py-2.5 text-right text-gray-300">{d.signed}</td>
                    <td className="py-2.5 text-right text-gray-400 text-xs">{d.previewed}</td>
                    <td className="py-2.5 text-right">
                      <Pct value={d.conversion_rate} good={50} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-gray-600 text-[10px] text-center">
        Ultimo aggiornamento: {new Date(data.generated_at).toLocaleString('it-IT')} · Auto-refresh ogni 60s
      </p>
    </div>
  );
};

export default SignatureStatsWidget;
